import { FC, useCallback, useEffect, useState } from "react";
import { Page } from "../../assets/components/PaginationBar/PaginationBar";
import { SearchBarDataHandler } from "../../assets/components/SearchBar/SearchBar";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import {
    useGetHistoryQuery,
    useGetUsersListQuery,
} from "../../redux/RTKQuery/api";
import { Loading, uiSlicedActions } from "../../redux/ui-slice";
import { FetchHistoryService } from "./model";
import Loader from "../../assets/components/Loader/Loader";
import PageTitle from "../../assets/components/PageTitle/PageTitle";
import { toast } from "react-toastify";
import HistorySearchBar from "./HistoryTable/HistorySearchBar";
import HistoryTableHeader from "./HistoryTable/HistoryTableHeader";
import HistoryTable from "./HistoryTable/HistoryTable";

const HistoryPage: FC = () => {
    const dispatch = useAppDispatch();
    const loading: Loading = useAppSelector((state) => state.ui.loading);

    const pageSize: number = 30;
    const [currentSizePage, setCurrentSizePage] = useState(pageSize);
    const [pageNumber, setPageNumber] = useState<Page>(1);
    const [dataTable, setData] = useState<FetchHistoryService["histories"]>([]);
    const [totalHistories, setTotalHistories] =
        useState<FetchHistoryService["totalHistories"]>(0);
    const [quiresData, setQuires] = useState<SearchBarDataHandler>({});

    const {
        data: histories,
        isError: isErrorHistory,
        isFetching: isFetchingHistory,
    } = useGetHistoryQuery(
        {
            pageNumber,
            pageSize,
            ...quiresData,
        },
        {
            refetchOnFocus: true,
        }
    );

    // Set page number to 1 when filtered licenses count changes
    useEffect(() => {
        setPageNumber(1);
    }, [histories?.filteredHistoriesCount]);

    const handleHistorySuccess = useCallback(() => {
        if (histories) {
            setData(histories.histories);
            const filteredCount = histories.filteredHistoriesCount;
            const totalCount = histories.totalHistories;

            if (filteredCount !== undefined) {
                setCurrentSizePage(() =>
                    totalHistories < pageSize ? filteredCount : pageSize
                );
                setTotalHistories(filteredCount);
            } else {
                setTotalHistories(totalCount);
            }
        }
    }, [histories, totalHistories, pageSize]);

    const handleHistoryError = useCallback(() => {
        if (isErrorHistory) {
            toast.error("Error loading history");
        }
    }, [isErrorHistory]);

    useEffect(() => {
        handleHistorySuccess();
        handleHistoryError();
    }, [handleHistorySuccess, handleHistoryError]);

    const {
        data: users = [],
        isLoading: isLoadingUsers,
        isError: isErrorUsers,
    } = useGetUsersListQuery("");

    useEffect(() => {
        if (isErrorUsers) {
            toast.error("Error loading users");
        }
    }, [isErrorUsers]);

    useEffect(() => {
        dispatch(
            uiSlicedActions.setLoading(
                isFetchingHistory || isLoadingUsers ? "pending" : "hidden"
            )
        );
    }, [isFetchingHistory, isLoadingUsers, dispatch]);

    return (
        <div className="page_with_table">
            <Loader loading={loading} />
            <PageTitle text="History" />
            <HistorySearchBar users={users} dataHandler={setQuires} />

            <div className="table_scroll_wrapper">
                <HistoryTableHeader
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    totalHistories={totalHistories}
                    setCurrentPage={setPageNumber}
                    currentSizePage={currentSizePage}
                    dataTableSize={dataTable.length}
                />
                <HistoryTable dataTable={dataTable} />
            </div>
        </div>
    );
};

export default HistoryPage;
